import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

const AdminDashboard = () => {
  const { t } = useTranslation();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/get_all_user_web`
      );

      if (res?.data?.status === "success") {
        setUsers(res.data.users || []);
      } else {
        setError(t("user_fetch_error"));
      }
    } catch (e) {
      setError(t("msg_server_or_network"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async (id) => {
    const ok = window.confirm(t("admin_confirm_delete"));
    if (!ok) return;

    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/delete_user_web/${id}`
      );

      if (res?.data?.success) {
        setUsers((prev) => prev.filter((u) => u.id !== id));
      } else {
        alert(t("delete_failed"));
      }
    } catch (e) {
      alert(t("delete_error"));
    }
  };

  const translateRole = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return t("urw_role_admin");
      case "user":
        return t("urw_role_user");
      case "technician":
        return t("role_technician");
      case "client":
        return t("role_client");
      default:
        return t("unknown");
    }
  };

  const translateType = (type) => {
    switch (type?.toLowerCase()) {
      case "direct":
        return t("urw_type_direct");
      case "both":
        return t("urw_type_both");
      case "scan":
        return t("urw_type_scan");
      default:
        return "-";
    }
  };

  const activeUsers = users.filter((user) => Number(user.is_activated) === 1).length;
  const qrTotal = users.reduce((total, user) => {
    if (user.role?.toLowerCase() === "admin") return total;
    return total + Number(user.qrcode_count || 0);
  }, 0);
  const appCount = new Set(users.map((user) => String(user.application || "").trim()).filter(Boolean)).size;

  return (
    <div className="gradient-hero min-h-[calc(100vh-150px)]">
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="text-2xl font-bold text-foreground">
          {t("admin_dashboard", "Tableau de bord admin")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("admin_manage_apps_users", "Gérer toutes les applications et utilisateurs")}
        </p>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="card-hover rounded-xl border border-border bg-card p-4 text-center">
            <p className="text-2xl font-bold text-primary">{users.length}</p>
            <p className="text-xs text-muted-foreground">{t("admin_users_total", "Utilisateurs")}</p>
          </div>
          <div className="card-hover rounded-xl border border-border bg-card p-4 text-center">
            <p className="text-2xl font-bold text-success">{activeUsers}</p>
            <p className="text-xs text-muted-foreground">{t("status_active", "Actifs")}</p>
          </div>
          <div className="card-hover rounded-xl border border-border bg-card p-4 text-center">
            <p className="text-2xl font-bold text-accent">{qrTotal}</p>
            <p className="text-xs text-muted-foreground">{t("admin_th_qr_count", "QR codes")}</p>
          </div>
          <div className="card-hover rounded-xl border border-border bg-card p-4 text-center">
            <p className="text-2xl font-bold text-foreground">{appCount}</p>
            <p className="text-xs text-muted-foreground">{t("admin_th_application", "Applications")}</p>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-card">
          {loading ? (
            <div className="flex items-center justify-center gap-3 px-4 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin text-primary" />
              {t("admin_loading_users")}
            </div>
          ) : error ? (
            <div className="px-4 py-6">
              <p className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
                {error}
              </p>
            </div>
          ) : users.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">{t("no_users")}</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("admin_th_id", "ID")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("admin_th_application")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("admin_th_type")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("admin_th_email")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("admin_th_city")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("admin_th_country")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("admin_th_status")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("admin_th_role")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("admin_th_qr_count")}</th>
                  <th className="px-4 py-3 text-start font-medium text-muted-foreground">{t("admin_th_actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => {
                  const isAdmin = user.role?.toLowerCase() === "admin";
                  const isActive = Number(user.is_activated) === 1;
                  return (
                    <tr key={user.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                        {String(user.id || index + 1).slice(0, 8)}
                      </td>
                      <td className="px-4 py-3 font-medium text-foreground">{user.application || "-"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{isAdmin ? "-" : translateType(user.type)}</td>
                      <td className="px-4 py-3 text-foreground">{user.email || "-"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{user.city || "-"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{user.country || "-"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            isActive ? "bg-success/10 text-success" : "bg-muted text-muted-foreground"
                          }`}
                        >
                          {isActive ? t("status_active") : t("status_not_active")}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          {translateRole(user.role)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground">{isAdmin ? "-" : user.qrcode_count ?? "-"}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDelete(user.id)}
                          className="text-xs text-destructive transition-colors hover:underline"
                        >
                          {t("delete")}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
